<script lang="ts">
  import Prose from '../lib/components/Prose.svelte';
  import ModexpFigure from '../lib/algorithms/modexp/Figure.svelte';
  import {
    MODEXP_CURVE_DATA,
    MODEXP_TOKEN_DATA,
    MODEXP_MODELS,
    MODEL_COLORS,
    type ModexpPoint,
    type ModexpTokenPoint,
  } from '../lib/data/modexp-curves';

  // Opus probe inputs drawn from a real run at mod 14,983 so the animation
  // walks the same algorithm the model executed on that trial.
  const opusModexpInputs = { base: 1546n, exp: 27n, mod: 14983n };

  const W = 760;
  const H = 420;
  const M = { top: 40, right: 120, bottom: 72, left: 64 };
  const PLOT_W = W - M.left - M.right;
  const PLOT_H = H - M.top - M.bottom;

  const X_MIN = 150;
  const X_MAX = 1e30;
  const LOG_MIN = Math.log10(X_MIN);
  const LOG_MAX = Math.log10(X_MAX);

  function xLog(v: number): number {
    const t = (Math.log10(v) - LOG_MIN) / (LOG_MAX - LOG_MIN);
    return M.left + t * PLOT_W;
  }
  function yRate(p: number): number {
    return M.top + (1 - p) * PLOT_H;
  }

  // Decade ticks across the full range; labels use scientific notation past
  // 1B so the axis stays readable at 28 decades of span.
  const X_TICKS = [1e3, 1e6, 1e9, 1e12, 1e15, 1e18, 1e21, 1e24, 1e27, 1e30];

  // Unicode superscript digits so SVG <text> can render 10²⁰ without tspan.
  const SUPER = ['⁰', '¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹'];
  function superExp(n: number): string {
    return String(n).split('').map((d) => SUPER[Number(d)]).join('');
  }
  function formatModulus(v: number): string {
    if (v >= 1e9) {
      const exp = Math.round(Math.log10(v));
      return `10${superExp(exp)}`;
    }
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1).replace(/\.0$/, '')}M`;
    if (v >= 1_000) return `${(v / 1000).toFixed(v >= 10_000 ? 0 : 1).replace(/\.0$/, '')}K`;
    return String(v);
  }

  type Series = {
    model: 'haiku' | 'sonnet' | 'opus';
    points: ModexpPoint[];
    color: string;
    linePath: string;
    bandPath: string;
  };

  function seriesFor(model: 'haiku' | 'sonnet' | 'opus'): Series {
    // Require N>=3 for plotting — single-trial cells produce Wilson CIs that
    // span 0-100% and dots that imply more data than we actually have.
    const points = MODEXP_CURVE_DATA.filter((p) => p.model === model && p.n >= 3)
      .slice()
      .sort((a, b) => a.modulus - b.modulus);
    const linePath =
      'M' + points.map((p) => `${xLog(p.modulus)},${yRate(p.accuracy)}`).join(' L');
    const top = points.map((p) => `${xLog(p.modulus)},${yRate(p.ciHi)}`);
    const bottom = points
      .map((p) => `${xLog(p.modulus)},${yRate(p.ciLo)}`)
      .reverse();
    const bandPath = `M${top.join(' L')} L${bottom.join(' L')} Z`;
    return {
      model,
      points,
      color: MODEL_COLORS[model] ?? '#000',
      linePath,
      bandPath,
    };
  }

  const series: Series[] = MODEXP_MODELS.map(seriesFor);

  // Legend anchor positions (right of plot)
  const LEGEND_X = M.left + PLOT_W + 18;
  function legendY(i: number) {
    return M.top + 12 + i * 22;
  }

  // ─── Token chart (shares x-axis geometry with the accuracy chart) ───
  // Y axis is log tokens; range goes to 200K so Sonnet's 128K cap lands on
  // the plot instead of getting clamped out of sight.
  const TOK_MIN = 100;
  const TOK_MAX = 200_000;
  const TOK_LOG_MIN = Math.log10(TOK_MIN);
  const TOK_LOG_MAX = Math.log10(TOK_MAX);

  function yTok(v: number): number {
    const clamped = Math.max(TOK_MIN, Math.min(TOK_MAX, v));
    const t = (Math.log10(clamped) - TOK_LOG_MIN) / (TOK_LOG_MAX - TOK_LOG_MIN);
    return M.top + (1 - t) * PLOT_H;
  }

  const Y_TOK_TICKS = [100, 1_000, 10_000, 100_000, 200_000];

  function formatTokens(v: number): string {
    if (v >= 1000) return `${(v / 1000).toFixed(v >= 10_000 ? 0 : 1).replace(/\.0$/, '')}K`;
    return String(v);
  }

  type TokenSeries = {
    model: 'haiku' | 'sonnet' | 'opus';
    points: ModexpTokenPoint[];
    color: string;
    linePath: string;
    bandPath: string;
  };

  function tokenSeriesFor(model: 'haiku' | 'sonnet' | 'opus'): TokenSeries {
    // Only include cells with N>=3 for readability — the token chart is
    // median-based and a single sample is too noisy to plot as a point.
    const points = MODEXP_TOKEN_DATA
      .filter((p) => p.model === model && p.n >= 3)
      .slice()
      .sort((a, b) => a.modulus - b.modulus);
    const linePath =
      'M' + points.map((p) => `${xLog(p.modulus)},${yTok(p.tokensMedian)}`).join(' L');
    const top = points.map((p) => `${xLog(p.modulus)},${yTok(p.tokensP75)}`);
    const bottom = points
      .map((p) => `${xLog(p.modulus)},${yTok(p.tokensP25)}`)
      .reverse();
    const bandPath = `M${top.join(' L')} L${bottom.join(' L')} Z`;
    return {
      model,
      points,
      color: MODEL_COLORS[model] ?? '#000',
      linePath,
      bandPath,
    };
  }

  const tokenSeries: TokenSeries[] = MODEXP_MODELS.map(tokenSeriesFor);
</script>

<section id="capability-curves" class="section">
  <Prose>
    <header class="header">
      <div class="eyebrow">Cost</div>
      <h2>The price of failing</h2>
      <p class="summary">
        Every model eventually reaches a task it cannot solve. The operational
        difference is how expensive that failure becomes.
      </p>
    </header>
    <p>
      Modular exponentiation — <em>a<sup>b</sup> mod m</em> — raises one
      number to a power, then keeps only the remainder after division by another.
      It is the arithmetic behind public-key cryptography: RSA, Diffie-Hellman,
      and HTTPS all depend on raising large numbers to large powers modulo large
      primes. The standard square-and-multiply algorithm writes the exponent in
      binary, walks the bits, squares the running value at each step, and
      multiplies by the base when the bit is 1.
    </p>
    <p>
      Trial difficulty has two knobs. The modulus controls the size of the
      intermediate products. The exponent controls how many products have to
      chain together. The pass rate is the two effects multiplied:
      p_step<sup>N</sup>, where p_step is per-multiplication accuracy at that
      operand size and N is the number of operations.
    </p>
  </Prose>

  <div class="figure-slot">
    <ModexpFigure inputs={opusModexpInputs} />
  </div>

  <Prose>
    <p>
      <strong>The formula also narrows the search.</strong> To find Opus's
      breaking point, I did not sweep modulus values and wait for the curve to
      fall apart. I used p_step<sup>N</sup> to predict the collapse. On
      twenty-digit operands, Opus's per-multiplication accuracy is too low for a
      seven-step chain to survive, so modulus 10<sup>20</sup> should fail. Three
      trials confirmed it. Predictability saved the dozens of calls a blind
      sweep would have spent.
    </p>
  </Prose>

  <div class="chart-wrap">
    <svg viewBox="0 0 {W} {H}" role="img" aria-label="Modular exponentiation pass rate by modulus across three models">
      <!-- Y grid + labels -->
      {#each [0, 0.25, 0.5, 0.75, 1] as t}
        <line
          x1={M.left} x2={M.left + PLOT_W}
          y1={yRate(t)} y2={yRate(t)}
          stroke="var(--border)" stroke-width="1" stroke-dasharray="2 3"
        />
        <text
          x={M.left - 8} y={yRate(t) + 3.5}
          text-anchor="end"
          class="axis-label"
        >{(t * 100).toFixed(0)}%</text>
      {/each}

      <!-- 70% reference line (capability boundary) -->
      <line
        x1={M.left} x2={M.left + PLOT_W}
        y1={yRate(0.7)} y2={yRate(0.7)}
        stroke="var(--viz-accent)" stroke-width="1" stroke-dasharray="3 4" stroke-opacity="0.55"
      />
      <text
        x={M.left + PLOT_W - 6}
        y={yRate(0.7) - 6}
        text-anchor="end"
        class="reference-label"
      >70% — useful boundary</text>

      <!-- X axis line -->
      <line
        x1={M.left} x2={M.left + PLOT_W}
        y1={M.top + PLOT_H} y2={M.top + PLOT_H}
        stroke="var(--ink-subtle)" stroke-width="1.2"
      />
      <!-- Y axis line -->
      <line
        x1={M.left} x2={M.left}
        y1={M.top} y2={M.top + PLOT_H}
        stroke="var(--ink-subtle)" stroke-width="1.2"
      />

      <!-- X tick labels -->
      {#each X_TICKS as t (t)}
        <line
          x1={xLog(t)} x2={xLog(t)}
          y1={M.top + PLOT_H} y2={M.top + PLOT_H + 4}
          stroke="var(--ink-subtle)" stroke-width="1"
        />
        <text
          x={xLog(t)} y={M.top + PLOT_H + 18}
          text-anchor="middle"
          class="axis-label"
        >{formatModulus(t)}</text>
      {/each}

      <!-- Axis titles -->
      <text
        transform="translate(16, {M.top + PLOT_H / 2}) rotate(-90)"
        text-anchor="middle"
        class="axis-title"
      >pass rate (N≥3 per point)</text>
      <text
        x={M.left + PLOT_W / 2}
        y={H - 10}
        text-anchor="middle"
        class="axis-title"
      >modulus (log scale) — harder →</text>

      <!-- CI bands (drawn first, behind lines) -->
      {#each series as s (s.model)}
        <path d={s.bandPath} fill={s.color} fill-opacity="0.12" />
      {/each}

      <!-- Lines + points -->
      {#each series as s (s.model)}
        <path d={s.linePath} fill="none" stroke={s.color} stroke-width="2" />
        {#each s.points as p (p.modulus)}
          <circle
            cx={xLog(p.modulus)} cy={yRate(p.accuracy)}
            r="4"
            fill={s.color}
            stroke="var(--bg)"
            stroke-width="1.5"
          >
            <title>{s.model} · modulus {p.modulus}: {p.passed}/{p.n} passed ({(p.accuracy * 100).toFixed(0)}%), average {p.avgTimeS.toFixed(1)}s</title>
          </circle>
        {/each}
      {/each}

      <!-- Legend -->
      {#each series as s, i (s.model)}
        <line
          x1={LEGEND_X} x2={LEGEND_X + 20}
          y1={legendY(i)} y2={legendY(i)}
          stroke={s.color} stroke-width="2.5"
        />
        <circle
          cx={LEGEND_X + 10} cy={legendY(i)}
          r="3.5"
          fill={s.color} stroke="var(--bg)" stroke-width="1.5"
        />
        <text
          x={LEGEND_X + 26} y={legendY(i) + 4}
          class="legend-label"
        >{s.model}</text>
      {/each}
    </svg>
  </div>

  <Prose>
    <p>
      Sonnet does not miss uniformly. Below a 50% pass rate, it often lands on
      the same wrong answer. The failure has direction, not just magnitude.
    </p>
  </Prose>

  <div class="chart-wrap">
    <svg viewBox="0 0 {W} {H}" role="img" aria-label="Median output tokens per modular exponentiation trial by modulus across three models">
      <!-- Y grid + labels (log-tokens) -->
      {#each Y_TOK_TICKS as t}
        <line
          x1={M.left} x2={M.left + PLOT_W}
          y1={yTok(t)} y2={yTok(t)}
          stroke="var(--border)" stroke-width="1" stroke-dasharray="2 3"
        />
        <text
          x={M.left - 8} y={yTok(t) + 3.5}
          text-anchor="end"
          class="axis-label"
        >{formatTokens(t)}</text>
      {/each}

      <!-- X axis line -->
      <line
        x1={M.left} x2={M.left + PLOT_W}
        y1={M.top + PLOT_H} y2={M.top + PLOT_H}
        stroke="var(--ink-subtle)" stroke-width="1.2"
      />
      <!-- Y axis line -->
      <line
        x1={M.left} x2={M.left}
        y1={M.top} y2={M.top + PLOT_H}
        stroke="var(--ink-subtle)" stroke-width="1.2"
      />

      <!-- X tick labels (shared with accuracy chart) -->
      {#each X_TICKS as t (t)}
        <line
          x1={xLog(t)} x2={xLog(t)}
          y1={M.top + PLOT_H} y2={M.top + PLOT_H + 4}
          stroke="var(--ink-subtle)" stroke-width="1"
        />
        <text
          x={xLog(t)} y={M.top + PLOT_H + 18}
          text-anchor="middle"
          class="axis-label"
        >{formatModulus(t)}</text>
      {/each}

      <!-- Axis titles -->
      <text
        transform="translate(16, {M.top + PLOT_H / 2}) rotate(-90)"
        text-anchor="middle"
        class="axis-title"
      >median output tokens per trial (log)</text>
      <text
        x={M.left + PLOT_W / 2}
        y={H - 10}
        text-anchor="middle"
        class="axis-title"
      >modulus (log scale) — harder →</text>

      <!-- IQR bands (P25–P75) -->
      {#each tokenSeries as s (s.model)}
        <path d={s.bandPath} fill={s.color} fill-opacity="0.12" />
      {/each}

      <!-- Median lines + points -->
      {#each tokenSeries as s (s.model)}
        <path d={s.linePath} fill="none" stroke={s.color} stroke-width="2" />
        {#each s.points as p (p.modulus)}
          <circle
            cx={xLog(p.modulus)} cy={yTok(p.tokensMedian)}
            r="4"
            fill={s.color}
            stroke="var(--bg)"
            stroke-width="1.5"
          >
            <title>{s.model} · modulus {p.modulus}: median {p.tokensMedian.toLocaleString()} tokens (IQR {p.tokensP25.toLocaleString()}–{p.tokensP75.toLocaleString()}), N={p.n}, median {p.durationMedianS}s</title>
          </circle>
        {/each}
      {/each}

      <!-- Legend -->
      {#each tokenSeries as s, i (s.model)}
        <line
          x1={LEGEND_X} x2={LEGEND_X + 20}
          y1={legendY(i)} y2={legendY(i)}
          stroke={s.color} stroke-width="2.5"
        />
        <circle
          cx={LEGEND_X + 10} cy={legendY(i)}
          r="3.5"
          fill={s.color} stroke="var(--bg)" stroke-width="1.5"
        />
        <text
          x={LEGEND_X + 26} y={legendY(i) + 4}
          class="legend-label"
        >{s.model}</text>
      {/each}
    </svg>
  </div>

  <Prose>
    <h3 class="subhead">When refusal appears</h3>
    <p>
      The pass-rate formula does not predict spend. Sonnet hits its 128K
      output-token cap on every trial: twenty-one minutes of work, then an
      answer nearly twenty orders of magnitude off. Opus produces a few hundred
      tokens in under a minute and stops. Same outcome, roughly four hundred
      times the cost.
    </p>
    <p>
      p_step<sup>N</sup> models success, not burn rate. Cost depends on a
      separate learned behavior: whether the model recognizes that a task is
      beyond its reach. Opus appears to have learned that some problems are not
      worth attempting, and at twenty-one digits it exits early. Sonnet did not
      learn the same boundary.
    </p>
    <p>
      Pass rate tells you whether the task will work. Refusal behavior tells you
      what failure will cost.
    </p>
  </Prose>
</section>

<style>
  .section {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: var(--space-4xl);
    width: 100%;
  }
  .header {
    margin-bottom: var(--space-lg);
  }
  .eyebrow {
    font-family: var(--font-sans);
    font-size: var(--text-xs);
    font-weight: var(--weight-medium);
    color: var(--ink-muted);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: var(--space-sm);
  }
  .summary {
    font-family: var(--font-serif);
    font-style: italic;
    font-size: var(--text-lg);
    color: var(--ink-muted);
    line-height: var(--leading-snug);
    margin-top: var(--space-sm);
    margin-bottom: var(--space-lg);
  }
  .chart-wrap {
    width: 100%;
    max-width: 880px;
    padding: var(--space-md);
    margin: var(--space-lg) 0;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    background: var(--surface);
  }
  .chart-wrap svg {
    width: 100%;
    height: auto;
    display: block;
  }
  .chart-wrap :global(.axis-label) {
    font-family: var(--font-sans);
    font-size: 11.5px;
    fill: var(--ink-muted);
  }
  .chart-wrap :global(.axis-title) {
    font-family: var(--font-sans);
    font-size: 12px;
    fill: var(--ink-muted);
  }
  .chart-wrap :global(.reference-label) {
    font-family: var(--font-sans);
    font-size: 10.5px;
    fill: var(--viz-accent);
  }
  .chart-wrap :global(.legend-label) {
    font-family: var(--font-sans);
    font-size: 12px;
    fill: var(--ink);
    text-transform: capitalize;
  }
  .caveat {
    font-family: var(--font-serif);
    font-style: italic;
    font-size: var(--text-sm);
    color: var(--ink-muted);
    line-height: var(--leading-snug);
    margin-top: var(--space-md);
  }
  :global(.prose) h3.subhead {
    font-family: var(--font-sans);
    font-size: var(--text-base);
    font-weight: var(--weight-medium);
    color: var(--ink);
    margin-top: var(--space-xl);
    margin-bottom: var(--space-sm);
  }
  .figure-slot {
    width: 100%;
    min-width: 0;
    max-width: 880px;
    margin: var(--space-md) 0;
  }
</style>
